import type { AttributeSchema } from "../../../../../../types/types.PIM";
import type { IRow } from "./type";

export type AurFilterRule = {
  attributeName: string | string[];
  matchColumns: string[];
  valueColumn: string;
  allowedCollections?: string[];
};

export type AddAllUnlessRestrictedOpts = {
  allConstraints?: { attribute_id: string; action: string }[];
  allColumns?: AttributeSchema[];
};

export const ALL_UNLESS_RESTRICTED = "All Unless Restricted";

/**
 * Rules driving which collection + attribute names get an AUR option and row duplicate filtering.
 * Add rules here when adding new AUR collections.
 */
export const CHOICE_FILTER_RULES: AurFilterRule[] = [
  {
    attributeName: "country",
    matchColumns: ["division", "region"],
    valueColumn: "country",
    allowedCollections: ["regulatory"],
  },
];

export function getAurRuleForColumn(
  collectionSchemaName: string,
  col: Pick<AttributeSchema, "name" | "display_name">
): AurFilterRule | undefined {
  const collectionLower = collectionSchemaName.toLowerCase();
  return CHOICE_FILTER_RULES.find((r) => {
    if (
      r.allowedCollections &&
      !r.allowedCollections.some((p) =>
        collectionLower.includes(p.toLowerCase())
      )
    ) {
      return false;
    }
    const names = Array.isArray(r.attributeName)
      ? r.attributeName
      : [r.attributeName];
    return names.some(
      (name) =>
        col.name.toLowerCase().includes(name.toLowerCase()) ||
        col.display_name?.toLowerCase().includes(name.toLowerCase())
    );
  });
}

export function columnGetsAllUnlessRestrictedChoice(
  collectionSchemaName: string,
  col: Pick<AttributeSchema, "name" | "display_name" | "choices">
): boolean {
  return (
    (col.choices ?? []).includes(ALL_UNLESS_RESTRICTED) ||
    getAurRuleForColumn(collectionSchemaName, col) !== undefined
  );
}

/**
 * True if some other attribute in the same AUR rule is `no_change`.
 */
export function anotherColumnInSameAurRuleHasNoChange(
  col: Pick<AttributeSchema, "id" | "name" | "display_name">,
  collectionSchemaName: string | undefined,
  allConstraints: { attribute_id: string; action: string }[] | undefined,
  allColumns: AttributeSchema[] | undefined
): boolean {
  if (!collectionSchemaName || !allConstraints?.length || !allColumns?.length) {
    return false;
  }
  const rule = getAurRuleForColumn(collectionSchemaName, col);
  if (!rule) {
    return false;
  }
  return allConstraints.some((c) => {
    if (c.action !== "no_change" || c.attribute_id === col.id) {
      return false;
    }
    const other = allColumns.find((x) => x.id === c.attribute_id);
    return !!other && getAurRuleForColumn(collectionSchemaName, other) === rule;
  });
}

/**
 * Skip `auto_fill` from API and autofill UI unless there is exactly one valid choice.
 */
export function shouldSkipAutofillForReferenceEntityConstraint(
  collectionSchemaName: string | undefined,
  allConstraints: { attribute_id: string; action: string }[] | undefined,
  columns:
    | {
        id: string;
        name?: string;
        display_name?: string | null;
        choices?: string[] | null;
      }[]
    | undefined,
  constraint: { attribute_id: string; action: string },
  filteredChoiceCount?: number
): boolean {
  if (
    !collectionSchemaName ||
    !allConstraints?.length ||
    !columns?.length ||
    constraint.action !== "auto_fill"
  ) {
    return false;
  }
  const toCol = (
    m: NonNullable<typeof columns[number]>
  ): Pick<AttributeSchema, "name" | "display_name" | "choices"> => ({
    name: m.name!,
    display_name: m.display_name ?? "",
    choices: m.choices ?? undefined,
  });
  const meta = columns.find((x) => x.id === constraint.attribute_id);
  if (
    !meta?.name ||
    !columnGetsAllUnlessRestrictedChoice(collectionSchemaName, toCol(meta))
  ) {
    return false;
  }
  const aurConstraints = allConstraints.filter((c) => {
    const m = columns.find((x) => x.id === c.attribute_id);
    return (
      !!m?.name &&
      columnGetsAllUnlessRestrictedChoice(collectionSchemaName, toCol(m))
    );
  });
  if (filteredChoiceCount === 1) {
    return false;
  }
  return (
    aurConstraints.length >= 2 &&
    aurConstraints.every((c) => c.action === "auto_fill")
  );
}

/**
 * Prepends AUR to choice lists when the collection/attribute rule applies.
 */
export function addAllUnlessRestrictedIfNeeded(
  choices: string[],
  collectionSchemaName: string,
  col: AttributeSchema,
  opts?: AddAllUnlessRestrictedOpts
): string[] {
  if (!collectionSchemaName) {
    return choices;
  }

  if (choices.includes(ALL_UNLESS_RESTRICTED)) {
    return [
      ALL_UNLESS_RESTRICTED,
      ...choices.filter((choice) => choice !== ALL_UNLESS_RESTRICTED),
    ];
  }

  const matchingRule = getAurRuleForColumn(collectionSchemaName, col);

  if (
    matchingRule &&
    !choices.includes(ALL_UNLESS_RESTRICTED) &&
    anotherColumnInSameAurRuleHasNoChange(
      col,
      collectionSchemaName,
      opts?.allConstraints,
      opts?.allColumns
    )
  ) {
    return choices;
  }

  if (matchingRule && !choices.includes(ALL_UNLESS_RESTRICTED)) {
    return [ALL_UNLESS_RESTRICTED, ...choices];
  }

  return choices;
}

/**
 * Filters choices based on CHOICE_FILTER_RULES (row duplicate / AUR column rules).
 * Removes values that already exist on other rows with matching column values.
 * Extensible via CHOICE_FILTER_RULES in this module.
 */
export const getFilteredChoices = ({
  col,
  currentRow,
  allRows,
  allColumns,
  watch,
  collectionSchemaName,
  constraint,
  allDependentConstraints,
  inputType,
}: {
  col: AttributeSchema;
  currentRow: IRow;
  allRows: IRow[] | undefined;
  allColumns: AttributeSchema[] | undefined;
  watch: (name: string) => any;
  collectionSchemaName?: string;
  constraint?: {
    action: string;
    valid_values?: { display: string }[];
  };
  allDependentConstraints?: { attribute_id: string; action: string }[];
  inputType?: "single_select" | "multi_select";
}): {
  filteredChoices: string[];
  nextValue: { type: "clear" | "autofill"; value: any } | null;
} => {
  if (!col.choices || col.choices.length === 0) {
    return { filteredChoices: [], nextValue: null };
  }

  // Add "All Unless Restricted" if collection and attribute match a rule
  const choices = addAllUnlessRestrictedIfNeeded(
    col.choices,
    collectionSchemaName ?? "",
    col,
    {
      allConstraints: allDependentConstraints,
      allColumns,
    }
  );

  if (!allRows || !allColumns) {
    return { filteredChoices: choices, nextValue: null };
  }

  const rule = getAurRuleForColumn(collectionSchemaName ?? "", col);
  if (!rule) {
    return { filteredChoices: choices, nextValue: null };
  }

  const findColumn = (name: string): AttributeSchema | undefined => {
    return allColumns.find(
      (c) =>
        c.name.toLowerCase() === name.toLowerCase() ||
        c.display_name?.toLowerCase() === name.toLowerCase() ||
        c.name.toLowerCase() === name.toLowerCase().replace(/_/g, " ") ||
        c.display_name?.toLowerCase() === name.toLowerCase().replace(/_/g, " ")
    );
  };

  const matchColumns = rule.matchColumns
    .map((name) => findColumn(name))
    .filter((c): c is AttributeSchema => c !== undefined);

  const valueColumn = findColumn(rule.valueColumn);

  if (matchColumns.length !== rule.matchColumns.length || !valueColumn) {
    return { filteredChoices: choices, nextValue: null };
  }

  const getValue = (colId?: string): string | null => {
    if (!colId) return null;

    const formValue = watch(colId);
    if (formValue) {
      if (typeof formValue === "object" && "value" in formValue) {
        return formValue.value || null;
      }
      if (Array.isArray(formValue) && formValue.length > 0) {
        return formValue[0]?.name || null;
      }
      if (typeof formValue === "string") {
        return formValue;
      }
    }

    const rowValue = currentRow[colId];
    if (rowValue) {
      if (Array.isArray(rowValue) && rowValue.length > 0) {
        return rowValue[0]?.name || null;
      }
      if (typeof rowValue === "string") {
        return rowValue;
      }
    }

    return null;
  };

  const getRowValue = (row: IRow, colId?: string): string | null => {
    if (!colId || !row[colId]) return null;
    const val = row[colId];
    if (Array.isArray(val) && val.length > 0) {
      return val[0]?.name || null;
    }
    return typeof val === "string" ? val : null;
  };

  const currentMatchValues = matchColumns.map((c) => getValue(c.id));

  // Require at least one match column to be filled
  const filledCount = currentMatchValues.filter((v) => v && v.trim()).length;
  if (filledCount === 0) {
    return { filteredChoices: choices, nextValue: null };
  }

  const normalizedCurrent = currentMatchValues.map((v) =>
    (v || "").toLowerCase().trim()
  );

  const filteredValues = new Set<string>();
  let matchFound = false;

  allRows.forEach((row) => {
    if (row.uuid === currentRow.uuid) {
      return;
    }

    const rowMatchValues = matchColumns.map((c) => getRowValue(row, c.id));
    const allFieldMatch = currentMatchValues.every(
      (curVal, index) =>
        (rowMatchValues[index] || "").toLowerCase().trim() ===
        normalizedCurrent[index]
    );
    const rowValue = getRowValue(row, valueColumn.id);
    if (allFieldMatch && rowValue) {
      filteredValues.add(rowValue.toLowerCase());
      matchFound = true;
    }
  });

  const filteredChoices = choices.filter((choice) => {
    return !filteredValues.has(choice.toLowerCase());
  });

  const shouldAutofillSingleFilteredChoice =
    constraint?.action === "auto_fill" &&
    filteredChoices.length === 1 &&
    !shouldSkipAutofillForReferenceEntityConstraint(
      collectionSchemaName,
      allDependentConstraints,
      allColumns,
      { attribute_id: col.id, action: constraint.action },
      filteredChoices.length
    );

  let nextValue: { type: "clear" | "autofill"; value: any } | null = null;

  // When match found, clear value column only if it has a value.
  // Do not clear when the API already auto-filled this field.
  if (
    matchFound &&
    col.id === valueColumn.id &&
    !shouldAutofillSingleFilteredChoice &&
    constraint?.action !== "auto_fill"
  ) {
    const currentValueCol = watch(valueColumn.id);
    const hasValue =
      inputType === "single_select"
        ? !!(
            currentValueCol?.value ??
            (typeof currentValueCol === "string" ? currentValueCol : null)
          )
        : Array.isArray(currentValueCol) && currentValueCol.length > 0;
    if (hasValue) {
      nextValue = {
        type: "clear",
        value: inputType === "single_select" ? null : [],
      };
    }
  }

  // auto_fill value if only one choice is left and action is auto_fill
  if (shouldAutofillSingleFilteredChoice) {
    const onlyChoice = filteredChoices[0];
    const currentValue = watch(col.id);
    if (inputType === "single_select") {
      const currentSingle =
        currentValue?.value ??
        (typeof currentValue === "string" ? currentValue : null);
      if (currentSingle !== onlyChoice) {
        nextValue = {
          type: "autofill",
          value: { label: onlyChoice, value: onlyChoice },
        };
      }
    } else if (inputType === "multi_select") {
      const names = Array.isArray(currentValue)
        ? currentValue.map((v: { name?: string } | string) =>
            typeof v === "string" ? v : v?.name || ""
          )
        : [];
      if (names.length !== 1 || names[0] !== onlyChoice) {
        nextValue = {
          type: "autofill",
          value:
            Array.isArray(currentValue) &&
            currentValue.length > 0 &&
            typeof currentValue[0] === "string"
              ? [onlyChoice]
              : [{ name: onlyChoice }],
        };
      }
    }
  }

  return { filteredChoices, nextValue };
};
